import React, { useEffect, useState } from 'react';
import { CalendarDays, ExternalLink, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { api } from '../../lib/api';
import { SectionHeader, EmptyState } from '../../components/Shared';

const fmtStamp = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Toronto' }) + ' ET'; } catch { return iso; }
};

const STATE_BADGE = {
  now: <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Happening now</Badge>,
  upcoming: <Badge className="bg-[#EAF2FF] text-[#1a2744] border-[#c7d7f5]">Upcoming</Badge>,
  past: <Badge variant="outline" className="border-slate-300 text-slate-500">Past</Badge>,
  not_yet_published: <Badge className="bg-amber-100 text-amber-800 border-amber-200">Not yet published by the City</Badge>,
};

const DateRow = ({ d, testId }) => (
  <Card className={d.state === 'past' ? 'bg-slate-50/70' : d.state === 'now' ? 'border-emerald-300' : ''} data-testid={testId} data-date-key={d.key}>
    <CardContent className="p-4 sm:p-5 flex gap-4">
      <span className={`inline-flex items-center justify-center h-12 w-12 rounded-xl shrink-0 ${d.state === 'past' ? 'bg-slate-200 text-slate-500' : 'bg-[#1a2744] text-[#C9A84C]'}`}><CalendarDays className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          {STATE_BADGE[d.state]}
          {d.approximate && <Badge variant="outline" className="border-amber-300 text-amber-800">Approximate (City wording)</Badge>}
          {d.source_check_status === 'changed' && <Badge className="bg-amber-100 text-amber-800 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" /> Source page changed, being re-checked</Badge>}
        </div>
        <p className={`font-bold text-[#1a2744] leading-snug ${d.state === 'past' ? 'text-slate-600' : ''}`} data-testid="key-date-title">{d.title}</p>
        <p className="text-sm text-[#1a2744] font-semibold mt-0.5" data-testid="key-date-date">{d.date_label}</p>
        {d.time_text && <p className="text-sm text-slate-600">{d.time_text}</p>}
        {d.description && <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d.description}</p>}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {(d.links || []).map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center">{l.label} <ExternalLink className="h-3 w-3 ml-1" /></a>)}
          {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer" className="text-slate-500 underline underline-offset-2 inline-flex items-center" data-testid="key-date-source">Source: {d.source_name || 'Official page'} <ExternalLink className="h-3 w-3 ml-1" /></a>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const Group = ({ title, items, testId, emptyText }) => (
  <div data-testid={testId}>
    <h3 className="text-lg font-bold text-[#1a2744] mb-3">{title} <span className="text-slate-400 font-semibold">({items.length})</span></h3>
    {items.length === 0 ? <p className="text-sm text-slate-500">{emptyText}</p> : <div className="space-y-3">{items.map((d) => <DateRow key={d.id || d.key} d={d} testId="key-date-item" />)}</div>}
  </div>
);

// Source-backed official dates only. Nothing here is estimated by this site.
export default function KeyDates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const load = () => {
    setLoading(true); setError(false);
    api.get('/election/dates').then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div className="space-y-3" data-testid="key-dates-loading">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}</div>;
  if (error || !data) {
    return (
      <div className="text-center py-10" data-testid="key-dates-error">
        <p className="font-bold text-[#1a2744]">Key dates could not be loaded.</p>
        <Button onClick={load} variant="outline" className="mt-3 h-11 border-[#1a2744] text-[#1a2744] font-bold" data-testid="key-dates-retry">Try again</Button>
      </div>
    );
  }
  const vp = data.voting_period || {};
  const total = (data.voters_upcoming?.length || 0) + (data.voters_past?.length || 0) + (data.candidates_upcoming?.length || 0) + (data.candidates_past?.length || 0);

  return (
    <div data-testid="election-key-dates">
      <SectionHeader kicker="MARK YOUR CALENDAR" title="Key election dates" dek="Every date below is read from official City of Kawartha Lakes pages and re-checked daily. Voter dates first, then candidate deadlines." />
      {total === 0 ? (
        <EmptyState title="No key dates published yet" subtitle="Official dates will appear here as the City publishes them." />
      ) : (
        <div className="space-y-8">
          <Card className="bg-[#1a2744] border-0" data-testid="voting-period-card"><CardContent className="p-6">
            <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]">Voting period</p>
            <p className="text-white text-xl sm:text-2xl font-bold mt-1">Friday, October 16 at 9:00 am to Monday, October 26, 2026 at 8:00 pm</p>
            <p className="text-slate-300 text-sm mt-1.5">Internet and telephone voting run 24 hours a day for the whole period. No paper ballots are offered by the City. In-person help is at City Hall, the Municipal Service Centres and selected library branches.</p>
            <a href={vp.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-slate-300 underline mt-3">Source: City of Kawartha Lakes, Key Dates <ExternalLink className="h-3 w-3 ml-1" /></a>
          </CardContent></Card>

          <Group title="For voters: coming up" items={data.voters_upcoming || []} testId="key-dates-voters-upcoming" emptyText="No upcoming voter dates." />
          <Group title="For candidates and third-party advertisers: coming up" items={data.candidates_upcoming || []} testId="key-dates-candidates-upcoming" emptyText="No upcoming candidate deadlines." />

          {((data.voters_past?.length || 0) + (data.candidates_past?.length || 0)) > 0 && (
            <div>
              <button type="button" onClick={() => setShowPast((v) => !v)} className="text-sm font-bold text-[#1a2744] underline underline-offset-2" data-testid="key-dates-toggle-past">
                {showPast ? 'Hide past dates' : `Show ${(data.voters_past?.length || 0) + (data.candidates_past?.length || 0)} past dates`}
              </button>
              {showPast && (
                <div className="mt-4 space-y-8">
                  <Group title="For voters: already passed" items={data.voters_past || []} testId="key-dates-voters-past" emptyText="None." />
                  <Group title="For candidates: already passed" items={data.candidates_past || []} testId="key-dates-candidates-past" emptyText="None." />
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-600 leading-relaxed" data-testid="key-dates-sources">
            <p className="font-bold text-[#1a2744] inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Where these dates come from</p>
            <ul className="mt-1.5 space-y-0.5">
              {(data.official_sources || []).map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer" className="underline">{s.name}</a></li>)}
            </ul>
            {data.refresh?.last_success_at && <p className="mt-2 inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Official pages last checked successfully {fmtStamp(data.refresh.last_success_at)}. Checked automatically every day at 6:00 pm Eastern.</p>}
            <p className="mt-2">{data.note}</p>
          </div>
        </div>
      )}
    </div>
  );
}
